import { type AxiosError, isAxiosError } from "axios";

import { type HTTPValidationError } from "@/api-v3/model";

const VALIDATION_STATUS = 422;

export const isApiValidationError = (
  error: unknown
): error is AxiosError<HTTPValidationError> => {
  if (!isAxiosError(error)) return false;
  if (error.response?.status !== VALIDATION_STATUS) return false;
  const detail = (error.response?.data as HTTPValidationError | undefined)
    ?.detail;
  return Array.isArray(detail) && detail.length > 0;
};

const snakeToCamel = (input: string) =>
  input.replace(/_([a-z])/g, (_, char: string) => char.toUpperCase());

export interface ApiValidationFieldError {
  field: string;
  /** `loc` minus the envelope, keeping parent groups and API casing. */
  path: string[];
  message: string;
  type: string;
}

export interface ApiValidationFormError {
  message: string;
  type: string;
}

export interface ApiValidationErrors {
  fieldErrors: ApiValidationFieldError[];
  formErrors: ApiValidationFormError[];
}

export interface GetApiValidationErrorsOptions {
  /** Override the default snake_case → camelCase mapping for specific API field names. */
  fieldNameMap?: Record<string, string>;
  /** Known form field names. Errors whose mapped name isn't here go to formErrors. */
  knownFields?: readonly string[];
}

export const getApiValidationErrors = (
  error: AxiosError<HTTPValidationError>,
  options: GetApiValidationErrorsOptions = {}
): ApiValidationErrors => {
  const details = error.response?.data?.detail ?? [];
  const fieldErrors: ApiValidationFieldError[] = [];
  const formErrors: ApiValidationFormError[] = [];

  for (const item of details) {
    // FastAPI loc starts with the envelope (body/query/path/...); only loc beyond that names a field.
    const last =
      item.loc.length > 1 ? item.loc[item.loc.length - 1] : undefined;

    if (typeof last !== "string") {
      formErrors.push({ message: item.msg, type: item.type });
      continue;
    }

    const field = options.fieldNameMap?.[last] ?? snakeToCamel(last);

    if (options.knownFields && !options.knownFields.includes(field)) {
      formErrors.push({ message: item.msg, type: item.type });
      continue;
    }

    const path = item.loc
      .slice(1)
      .filter((segment): segment is string => typeof segment === "string");

    fieldErrors.push({ field, path, message: item.msg, type: item.type });
  }

  return { fieldErrors, formErrors };
};
